import { useEffect, useState } from 'react';
import {
  Actions,
  Async,
  FSpread,
  toAsyncFetched,
} from '@client/new/helpers/types';
import { OtpBundlesModel, useOtpBundlesModel } from '../model/otpBundles.model';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { isEmpty } from 'lodash';
import { ColorsVars } from '@design-system/vars.css';

import {
  filterBundles,
  FormCta,
  getOfferLinks,
  ListFilter,
  toFormContent,
  toFormContentEmptyState,
  toFormCta,
  toFormFeatures,
  toFormOnChangeArray,
  toFormPrograms,
  toFormProgramsEmptyState,
  toFormSelectedContent,
  toFormSelectedPrograms,
  toListFilterTabs,
  toOTPBundleItems,
} from './otpBundle.vm.mapper';
import toast from 'react-hot-toast';
import { navigate, PATHS } from '@client/routes';

export type BundleCardItemFeatures = Array<{
  id: number;
  name: string;
  checked: boolean;
}>;

export type Submitting = 'submitting';
export type Standard = 'standard';

export type StandardBundleCardData = {
  type: Standard;
  title: string;
  price: string;
  details: string;
  status: {
    label: string;
    color: {
      text: ColorsVars;
      bg: ColorsVars;
    };
  };
  programs: Array<{
    title: string;
    tooltipItems?: string[];
  }>;
  content: Array<{
    title: string;
    tooltipItems?: string[];
  }>;
  features: BundleCardItemFeatures;
};

export type BundleCardItem = {
  onClick: () => void;
  menu: Array<{
    icon: IconProp;
    disabled: boolean;
    onClick?: () => void;
    tooltip?: string | string[];
  }>;
  data: { type: Submitting; title: string } | StandardBundleCardData;
};

export type FormDataMultiSelect<T> = {
  sectionTitle: string;
  emptyState: {
    urlLabel: string;
    label: string;
    onClick: () => void;
  };
  options: Array<{
    name: string;
    id: T;
  }>;
  toFormOnChange: (value: T, selectedValues: T[]) => T[];
  selectedItems: Array<{
    name: string;
    id: T;
    disableDelete: boolean;
  }>;
};

type Links = Array<{
  title: string;
  url: string;
  description: string;
  copy: () => void;
  edit?: () => void;
}>;

export type OtpBundlesVMData = {
  add: () => void;
  emptyState: {
    show: boolean;
    addBundle: () => void;
  };
  formData: {
    readonly: boolean;
    showFeeControl: boolean;
    control: OtpBundlesModel['control'];
    open: boolean;
    cancel: () => void;
    title: string;
    cta: FormCta;
    formError: string | string[] | null;
    formErrorTitle: string | null;
    data: {
      programs: FormDataMultiSelect<number>;
      content: FormDataMultiSelect<string>;
      features: {
        options: Array<{
          name: string;
          id: number;
        }>;
        toFormOnChange: (value: number, selectedValues: number[]) => number[];
      };
    };
    discounts?: {
      count: number;
      onShow: () => void;
      onCreate: () => void;
    };
  };
  list: {
    activeTab: string;
    changeTab: (value: string) => void;
    items: Array<BundleCardItem>;
    filterTabs: Array<ListFilter>;
  };
  linksModal: {
    showLinks: boolean;
    data: {
      links: Links;
    };
    isOpen: boolean;
    onClose: () => void;
  };
  deleteModal: {
    isOpen: boolean;
    confirm: () => void;
    cancel: () => void;
  };
  archiveModal: {
    isOpen: boolean;
    confirm: () => void;
    cancel: () => void;
  };
  publishModal: {
    isOpen: boolean;
    confirm: () => void;
    cancel: () => void;
    draftItems: string[];
  };
  unsavedChangesModal: {
    isOpen: boolean;
    saveChanges: () => void;
    discard: () => void;
    cancel: () => void;
  };
};

type PayloadMap = {
  addOtpBundle: null;
  cancelBundleChanges: null;
  setOtpTab: string;
  editBundle: number;
  openLinksModal: number;
  closeLinksModal: null;
  duplicateBundle: number;
  saveBundle: null;
  publishBundle: null;
  archiveBundle: null;
  confirmArchiveBundle: null;
  confirmPublishBundle: null;
  deleteBundle: null;
  confirmDeleteBundle: null;
  editLandingPage: null;
  copyToClipboard: string;
  confirmRedirect: string;
  confirmSaveChanges: null;
  confirmOfferChanges: null;
  redirect: string;
  discardChanges: null;
};

export type BundlesVMAction = Actions<PayloadMap>;
export type BundlesVMDispatcher = FSpread<PayloadMap>;

export type OtpBundlesVM = OtpBundlesVMData & {
  dispatch: BundlesVMDispatcher;
};

export type BundleVMState = {
  activeOtpTab: string;
  bundleLinksModal: number | null;
  confirmDelete: boolean;
  confirmArchive: boolean;
  confirmPublishContent: string[];
  redirectPath: string | null;
  confirmSaveChanges: boolean;
};

export const useOtpBundlesVM = ({
  creatorId,
}: {
  creatorId: number;
}): Async<OtpBundlesVM> => {
  const hashBundleId = window.location.hash.split('#').pop();
  const [state, setState] = useState<BundleVMState>({
    activeOtpTab: 'All',
    bundleLinksModal: null,
    confirmDelete: false,
    confirmArchive: false,
    confirmPublishContent: [],
    redirectPath: null,
    confirmSaveChanges: false,
  });

  const model = useOtpBundlesModel({ creatorId });

  useEffect(() => {
    if (hashBundleId && !isNaN(Number(hashBundleId))) {
      if (model.type === 'fetched') {
        model.value.dispatch('bundleForm', {
          mode: 'edit',
          bundleId: Number(hashBundleId),
        });
      }
    }
  }, [model.type]);

  if (model.type !== 'fetched') return model;

  const {
    dispatch: modelDispatch,
    allPrograms,
    sellablePrograms,
    otpBundles,
    allFeatures,
    allContent,
    sellableContent,
    form,
    submittedNewBundle,
    submittedEditBundle,
  } = model.value;

  const publishBundle = () => {
    const notPublishedContent = sellableContent
      .filter((c) => form.data.content.includes(c.id))
      .filter((c) => !c.isPublished)
      .map((c) => c.name);

    const notPublishedPrograms = sellablePrograms
      .filter((c) => form.data.programs.includes(c.id))
      .filter((c) => !c.isPublished)
      .map((c) => c.name);

    if (isEmpty(notPublishedContent) && isEmpty(notPublishedPrograms)) {
      modelDispatch('publishBundle');
      return;
    }
    setState((prev) => ({
      ...prev,
      confirmPublishContent: [...notPublishedContent, ...notPublishedPrograms],
    }));
  };

  const dispatch: BundlesVMDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as BundlesVMAction;
    switch (action.type) {
      case 'addOtpBundle':
        modelDispatch('bundleForm', { mode: 'create', bundleId: null });
        window.history.pushState(null, '', window.location.pathname);
        return null;
      case 'cancelBundleChanges':
        if (!form.unsavedChanges) {
          modelDispatch('bundleForm', { mode: null, bundleId: null });
          window.history.pushState(null, '', window.location.pathname);
        } else {
          setState((prev) => ({
            ...prev,
            confirmSaveChanges: true,
          }));
        }
        return null;
      case 'setOtpTab':
        setState((prev) => ({
          ...prev,
          activeOtpTab: action.payload,
        }));
        return null;
      case 'editBundle':
        modelDispatch('bundleForm', { mode: 'edit', bundleId: action.payload });
        window.history.pushState(null, '', '#' + action.payload);
        return null;
      case 'duplicateBundle':
        modelDispatch('bundleForm', {
          mode: 'duplicate',
          bundleId: action.payload,
        });
        return null;
      case 'openLinksModal':
        setState((prev) => ({
          ...prev,
          bundleLinksModal: action.payload,
        }));
        return null;
      case 'closeLinksModal':
        setState((prev) => ({
          ...prev,
          bundleLinksModal: null,
        }));
        return null;
      case 'saveBundle':
        modelDispatch(action.type);
        return null;
      case 'publishBundle':
        publishBundle();
        return null;
      case 'archiveBundle':
        setState((prev) => ({ ...prev, confirmArchive: true }));
        return null;
      case 'deleteBundle':
        setState((prev) => ({ ...prev, confirmDelete: true }));
        return null;
      case 'editLandingPage':
        if (form.bundleId || state.bundleLinksModal)
          navigate(
            PATHS.OFFER_LANDING_PAGE_EDITOR(
              creatorId,
              form.bundleId || state.bundleLinksModal || 0,
            ),
          );
        return null;
      case 'copyToClipboard':
        navigator.clipboard.writeText(action.payload);
        toast.success('Link was copied to clipboard');
        return null;
      case 'confirmDeleteBundle':
        setState((prev) => ({ ...prev, confirmDelete: false }));
        modelDispatch('deleteBundle');
        return null;
      case 'confirmArchiveBundle':
        setState((prev) => ({ ...prev, confirmArchive: false }));
        modelDispatch('archiveBundle');
        return null;
      case 'confirmPublishBundle':
        setState((prev) => ({ ...prev, confirmPublishContent: [] }));
        modelDispatch('publishBundle');
        return null;
      case 'redirect':
        navigate(action.payload);
        return null;
      case 'confirmSaveChanges':
        setState((prev) => ({ ...prev, confirmSaveChanges: true }));
        return null;
      case 'confirmRedirect':
        setState((prev) => ({
          ...prev,
          redirectPath: action.payload,
          confirmSaveChanges: true,
        }));
        return null;
      case 'confirmOfferChanges': {
        if (state?.redirectPath) {
          modelDispatch('saveBundleWithAction', {
            callback: () => navigate(state.redirectPath as string),
          });
          return null;
        }
        setState((prev) => ({ ...prev, confirmSaveChanges: false }));
        modelDispatch('saveBundleWithAction', {
          callback: () => {
            window.history.pushState(null, '', window.location.pathname);
          },
        });
        return null;
      }
      case 'discardChanges': {
        setState((prev) => ({ ...prev, confirmSaveChanges: false }));
        if (state.redirectPath) {
          navigate(state.redirectPath);
          return null;
        }
        modelDispatch('bundleForm', { mode: null, bundleId: null });
        window.history.pushState(null, '', window.location.pathname);
        return null;
      }
    }
  };

  const filteredBundles = filterBundles(state.activeOtpTab, otpBundles);
  const formEditMode = form.mode === 'edit';

  const extrasPath = PATHS.EXTRAS(creatorId);
  const otpPath = PATHS.NEW_PROGRAM(creatorId, { salesType: 'otp' });
  const landingPagePath = PATHS.OFFER_LANDING_PAGE_EDITOR(
    creatorId,
    form.bundleId || state.bundleLinksModal || 0,
  );
  const showDiscountsPath = PATHS.PROMOTIONS(creatorId, {
    mode: 'list',
    search: '"' + form.bundleName + '"',
  });
  const createDiscountPath = PATHS.PROMOTIONS(creatorId, {
    mode: 'create',
    offerId: '' + form.bundleId,
  });

  return toAsyncFetched<OtpBundlesVM>({
    dispatch,
    add: () => dispatch('addOtpBundle'),
    formData: {
      readonly: model.value.form.readonly,
      showFeeControl: model.value.isAdmin,
      control: model.value.control,
      open: !!form.mode,
      cancel: () => dispatch('cancelBundleChanges'),
      title: form.readonly
        ? 'Archived offer'
        : formEditMode
        ? 'Edit offer'
        : form.bundleId
        ? 'Duplicate offer'
        : 'Create new offer',
      cta: toFormCta(formEditMode, dispatch, form, landingPagePath),
      formError: form.error,
      formErrorTitle: form.published
        ? 'Offer cannot be saved'
        : 'Offer cannot be published',
      data: {
        programs: {
          sectionTitle: 'Programs',
          emptyState: toFormProgramsEmptyState(
            dispatch,
            form.unsavedChanges,
            otpPath,
          ),
          options: toFormPrograms(sellablePrograms),
          toFormOnChange: toFormOnChangeArray,
          selectedItems: toFormSelectedPrograms(allPrograms, form),
        },
        content: {
          sectionTitle: 'Extras',
          emptyState: toFormContentEmptyState(
            dispatch,
            form.unsavedChanges,
            extrasPath,
          ),
          options: toFormContent(sellableContent),
          toFormOnChange: toFormOnChangeArray,
          selectedItems: toFormSelectedContent(allContent, form),
        },
        features: {
          options: toFormFeatures(allFeatures),
          toFormOnChange: toFormOnChangeArray,
        },
      },
      discounts: formEditMode
        ? {
            count: form.discountsCount,
            onShow: () =>
              form.unsavedChanges
                ? dispatch('confirmRedirect', showDiscountsPath)
                : dispatch('redirect', showDiscountsPath),
            onCreate: () =>
              form.unsavedChanges
                ? dispatch('confirmRedirect', createDiscountPath)
                : dispatch('redirect', createDiscountPath),
          }
        : undefined,
    },
    emptyState: {
      show: isEmpty(otpBundles),
      addBundle: () => dispatch('addOtpBundle'),
    },
    list: {
      filterTabs: toListFilterTabs(otpBundles),
      activeTab: state.activeOtpTab,
      changeTab: (value: string) => dispatch('setOtpTab', value),
      items: toOTPBundleItems(
        filteredBundles,
        allPrograms,
        allFeatures,
        allContent,
        dispatch,
        submittedNewBundle,
        submittedEditBundle,
      ),
    },
    linksModal: {
      showLinks: true,
      isOpen: !!state.bundleLinksModal,
      onClose: () => dispatch('closeLinksModal'),
      data: {
        links: getOfferLinks(
          dispatch,
          otpBundles.find((b) => b.id === state.bundleLinksModal),
        ),
      },
    },
    deleteModal: {
      isOpen: !!state.confirmDelete,
      confirm: () => dispatch('confirmDeleteBundle'),
      cancel: () => setState((prev) => ({ ...prev, confirmDelete: false })),
    },
    archiveModal: {
      isOpen: !!state.confirmArchive,
      confirm: () => dispatch('confirmArchiveBundle'),
      cancel: () => setState((prev) => ({ ...prev, confirmArchive: false })),
    },
    publishModal: {
      isOpen: !isEmpty(state.confirmPublishContent),
      confirm: () => dispatch('confirmPublishBundle'),
      cancel: () =>
        setState((prev) => ({ ...prev, confirmPublishContent: [] })),
      draftItems: state.confirmPublishContent || [],
    },
    unsavedChangesModal: {
      isOpen: !!state.confirmSaveChanges,
      saveChanges: () => dispatch('confirmOfferChanges'),
      discard: () => dispatch('discardChanges'),
      cancel: () =>
        setState((prev) => ({ ...prev, confirmSaveChanges: false })),
    },
  });
};
